/* eslint-disable lingui/no-unlocalized-strings -- contratos técnicos HTTP */
import type { StepUpGrant } from '@/shared/http';
import { creditApi } from '@/shared/http/client';
import { wire, withSignal } from '@/shared/reference/wire';
import {
  groupMemberPageSchema,
  userGroupOptionPageSchema,
  userGroupPageSchema,
  userGroupSchema,
  type GroupMemberListParams,
  type UserGroupBody,
  type UserGroupListParams,
} from './user-group.wire';
export async function listUserGroupOptions(
  options: { tenant: string | undefined },
  signal?: AbortSignal,
) {
  const data = await creditApi.get<unknown>('/UserGroup', {
    params: { page: 0, size: 10, tenant: options.tenant },
    ...withSignal(signal),
  });
  return wire(userGroupOptionPageSchema, data, 'GET /UserGroup');
}

// U02: ClonePermissionContainer.tsx:34 + groupContext.tsx:139-146 (a busca vai como searchString).
export async function searchUserGroupOptions(
  options: { tenant: string | undefined; searchString?: string | undefined },
  signal?: AbortSignal,
) {
  const params: Record<string, unknown> = {};
  if (options.searchString) params['searchString'] = options.searchString;
  Object.assign(params, { page: 0, size: 10, tenant: options.tenant });
  const data = await creditApi.get<unknown>('/UserGroup', { params, ...withSignal(signal) });
  return wire(userGroupOptionPageSchema, data, 'GET /UserGroup');
}

// U02: services/user/userGroups.ts:16-24; a ordem vem do groupContext.tsx:139-151.
export async function listUserGroups(params: UserGroupListParams, signal?: AbortSignal) {
  const query: Record<string, unknown> = {};
  if (params.searchString) query['searchString'] = params.searchString;
  Object.assign(query, {
    page: params.page,
    size: params.size,
    paginationToken: params.tenant === undefined ? params.paginationToken : undefined,
    tenant: params.tenant,
  });
  const data = await creditApi.get<unknown>('/UserGroup', { params: query, ...withSignal(signal) });
  return wire(userGroupPageSchema, data, 'GET /UserGroup');
}

// U02: services/group/index.ts:5-14 (nome cru no caminho, como o legado).
export async function getUserGroup(groupName: string, tenant: string | undefined, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>(`/UserGroup/${groupName}`, {
    params: { tenant },
    routeTemplate: '/UserGroup/{groupName}',
    ...withSignal(signal),
  });
  return wire(userGroupSchema, data, 'GET /UserGroup/{groupName}');
}

const withStepUp = (stepUp: StepUpGrant | undefined) => (stepUp ? { stepUp } : {});

// U02: services/group/index.ts:23-35; `groupId === 'novo'` cria. `[RequireStepUp(GroupWrite)]` no POST e, no PUT,
// preso ao `{groupNameId}` da rota (`UserGroupController.cs:162-204`).
export async function saveUserGroup(
  groupId: string | undefined,
  body: UserGroupBody,
  tenant: string | undefined,
  stepUp?: StepUpGrant,
) {
  const data = groupId
    ? await creditApi.put<unknown>(`/UserGroup/${groupId}`, body, {
        params: { tenant },
        routeTemplate: '/UserGroup/{groupName}',
        ...withStepUp(stepUp),
      })
    : await creditApi.post('/UserGroup', body, { params: { tenant }, ...withStepUp(stepUp) });
  return data;
}

// U02: services/group/index.ts:69-77. `[RequireStepUp(GroupWrite, ResourceRouteParam = "id")]` (`UserGroupController.cs:230-237`).
export async function deleteUserGroup(groupName: string, tenant: string | undefined, stepUp?: StepUpGrant) {
  await creditApi.delete(`/UserGroup/${groupName}`, {
    params: { tenant },
    routeTemplate: '/UserGroup/{groupName}',
    ...withStepUp(stepUp),
  });
}

// U02: services/group/index.ts:57-65; groupContext.tsx:85 põe o tenant por último.
export async function listGroupMembers(
  groupName: string,
  params: GroupMemberListParams,
  signal?: AbortSignal,
) {
  const data = await creditApi.get<unknown>(`/UserGroup/GetUsersByGroup/${groupName}`, {
    params: { page: params.page, size: params.size, nextToken: params.nextToken, tenant: params.tenant },
    routeTemplate: '/UserGroup/GetUsersByGroup/{groupName}',
    ...withSignal(signal),
  });
  return wire(groupMemberPageSchema, data, 'GET /UserGroup/GetUsersByGroup/{groupName}');
}

// U02: services/user/userGroups.ts:39-49 (corpo `{}`). `GroupMembership` preso ao `{userId}` (`UserGroupController.cs:262-270`).
export async function insertGroupMember(groupName: string, userId: string, tenant: string | undefined, stepUp?: StepUpGrant) {
  await creditApi.post(`/UserGroup/${groupName}/InsertUser/${userId}`, {}, {
    params: { tenant },
    routeTemplate: '/UserGroup/{groupName}/InsertUser/{userId}',
    ...withStepUp(stepUp),
  });
}

// U02: services/user/userGroups.ts:27-37. `GroupMembership` preso ao `{userId}` (`UserGroupController.cs:298-306`).
export async function removeGroupMember(groupName: string, userId: string, tenant: string | undefined, stepUp?: StepUpGrant) {
  await creditApi.delete(`/UserGroup/${groupName}/RemoveUser/${userId}`, {
    params: { tenant },
    routeTemplate: '/UserGroup/{groupName}/RemoveUser/{userId}',
    ...withStepUp(stepUp),
  });
}
